/**
 * Node's copy of the completion compute (field-crypto plan 7.1, 1.2.1). The
 * marked region is byte-identical to `convex-silo/completionCompute.ts.template`
 * (tests/jobs/completionComputeParity.test.ts): the silo runs it on plaintext
 * conversation rows, Node on opened sealed ones.
 */

// COMPLETION_COMPUTE_START
/**
 * Completion compute over conversation content (field-crypto plan 7.1, 1.2.1).
 * One pure region, byte-identical in `src/features/jobs/completionCompute.ts`
 * (tests/jobs/completionComputeParity.test.ts): the silo runs it on plaintext
 * conversation rows, Node on opened sealed ones, so the two paths cannot
 * drift. No imports: hashing and canonical JSON are passed in.
 */

export const COMPLETION_NOTIFICATION_SUMMARY_MAX = 1000;
export const COMPLETION_NOTIFICATION_FALLBACK_SUMMARY = "A task has an update.";

/**
 * `notifications.summary` for a job's completion: the text of the turn's own
 * `workflowCompletions` entry (the first that lists the job), cut to
 * {@link COMPLETION_NOTIFICATION_SUMMARY_MAX} characters, or a constant.
 */
export function completionNotificationSummary(workflowCompletions: unknown, jobId: string): string {
  if (!Array.isArray(workflowCompletions)) return COMPLETION_NOTIFICATION_FALLBACK_SUMMARY;
  const own: unknown = workflowCompletions.find((entry: unknown) => {
    const jobIds = entry && typeof entry === "object" ? (entry as { jobIds?: unknown }).jobIds : undefined;
    return Array.isArray(jobIds) && jobIds.includes(jobId);
  });
  const text = own && typeof own === "object" ? (own as { textAnswer?: unknown }).textAnswer : undefined;
  return typeof text === "string" && text.trim() ? text.slice(0, COMPLETION_NOTIFICATION_SUMMARY_MAX) : COMPLETION_NOTIFICATION_FALLBACK_SUMMARY;
}

/** A completion notification's lifetime in the feed. */
export const COMPLETION_NOTIFICATION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * A job completion's `notifications` id, one per job and source revision, so
 * each revision has its own read and dismiss state. It keeps its form when
 * the table seals (`PLAINTEXT_ID_PREFIXES`): the silo finds it by id.
 */
export function completionNotificationId(jobId: string, sourceFingerprint: string): string {
  return `job-completion:${jobId}:${sourceFingerprint}`;
}

/** What a completion notification is built from. */
export type CompletionNotificationInput = {
  id: string;
  jobId: string;
  userId: string;
  /** The silo's own write names the turn's company; Node leaves it to the server identity. */
  companyId?: string;
  summary: string;
  now: number;
  /** The scene the completion resolved to, or null. */
  workspaceSceneId: string | null;
  /** The turn the completion merged into. */
  reactId?: string;
  /** A stored row under the same id: its creation date and handoff revision carry forward. */
  previous?: { createdDate?: unknown; revision?: unknown } | null;
};

/**
 * The completion's notification document: a digest `task_update` whose
 * continuity hand-off opens the job. One builder for the silo's publish (old
 * Node callers) and Node's (plan 8b.6), so the two write the same row.
 */
export function completionNotificationDocument(input: CompletionNotificationInput) {
  const { id, jobId, now } = input;
  const previousRevision = input.previous?.revision;
  const revision = typeof previousRevision === "number" && Number.isSafeInteger(previousRevision) && previousRevision >= 0 ? previousRevision + 1 : 0;
  const createdDate = typeof input.previous?.createdDate === "number" ? input.previous.createdDate : now;
  const continuityHandoff = { handoffId: id, notificationId: id, dedupeKey: id, status: "pending", revision, updatedAt: now,
    target: { kind: "job", id: jobId, webPath: `/?continuity=job&jobId=${encodeURIComponent(jobId)}` } };
  return {
    id, userId: input.userId, ...(input.companyId ? { companyId: input.companyId } : {}),
    source: "smartmanager", notificationType: "task_update", presentationMode: "digest", summary: input.summary,
    urgency: 2, actionRequired: false, sourceObjectType: "job", sourceObjectId: jobId, dedupeKey: id,
    storedDate: now, createdDate, updatedDate: now, expiresAt: now + COMPLETION_NOTIFICATION_TTL_MS,
    extraFields: { continuityHandoff, workspaceSceneId: input.workspaceSceneId, ...(input.reactId !== undefined ? { reactId: input.reactId } : {}) },
  };
}

/** Hashing and canonical JSON, passed in: the silo's `providerRuntime`, Node's `completionComputeDeps`. */
export type CompletionComputeDeps = {
  sha256Hex(value: string): Promise<string>;
  canonicalJson(value: unknown, maxBytes?: number): string;
};

/** What the undo-verification generation binds: the turn's actions and their verified receipts. */
export type UndoGenerationInput = { ownerId: string; companyId: string; requestReactId: string; fetchedData: unknown[]; receipts: unknown[] };

/**
 * Finalization also takes the verification generation: the bare sha256 of
 * {@link plainUndoGeneration} on a plaintext turn, a keyed `cd1:` digest
 * ("undoGeneration") on a sealed one (plan 1.2.1), so a sealed turn's
 * checkpoint is no guess oracle for its content.
 */
export type CompletionUndoDeps = CompletionComputeDeps & {
  undoGeneration(input: UndoGenerationInput): Promise<string>;
};

/** Byte cap of the aggregate undo hashes (generation, audio key, request fingerprint). */
export const COMPLETION_UNDO_AGGREGATE_BYTES = 1024 * 1024;
/** Byte cap of a finalization result Node hands the silo to compare (a turn row is at most 1 MiB). */
export const COMPLETION_UNDO_RESULT_BYTES = 4 * 1024 * 1024;

/** The generation of a plaintext turn: a bare sha256 of its canonical input. */
export async function plainUndoGeneration(input: UndoGenerationInput, deps: CompletionComputeDeps): Promise<string> {
  return await deps.sha256Hex(deps.canonicalJson(input, COMPLETION_UNDO_AGGREGATE_BYTES));
}

type Loose = Record<string, unknown>;
/** `value?.[key]` over stored JSON of unknown shape. */
function prop(value: unknown, key: string): unknown {
  return value !== null && typeof value === "object" ? (value as Loose)[key] : undefined;
}
/** A stored value as a spread source; a cast only, so spreading keeps its runtime meaning. */
const loose = (value: unknown) => value as Loose;

export type UndoSource = { jobId: string; workflowResultId?: string; sourceFingerprint: string; effectId: string };
export type UndoProof = { jobId: string; sourceFingerprint: string; effectId: string; componentId: string; fingerprint: string; completedAt: number };
export type UndoEntry = { effectId: string; componentId: string; appliedFingerprint?: string; undoneFingerprint?: string; undoneAt?: number };
export type UndoDelivery = { effectId: string; componentIds: string[] };
export type UndoVerificationCheckpoint = { version: 1; generation: string; nextIndex: number };
export type PreparedUndoAction = {
  action: "undo_component"; status: "pending"; componentId: unknown;
  undoRequest: { reactId: unknown; previewId: unknown };
  undoTarget: { ownerId: unknown; companyId: unknown; componentId: unknown;
    undo: { source: UndoSource; workspaceSceneId: unknown; fingerprint: unknown; state: "ready" } };
};

/**
 * One effect's job-side state, as the silo's `current()` reads it for a
 * captured source: the job's conversation company and scene, and the
 * effect's stored undo and delivery entries (null when absent).
 */
export type UndoJobView = { companyId: unknown; workspaceSceneId: unknown; undo: UndoEntry | null; delivery: UndoDelivery | null };

/** One undo's proof digest: binds the request turn, its preview and the completed write. */
export async function undoProofDigest(requestReactId: string, previewId: string, proof: UndoProof, deps: CompletionComputeDeps) {
  return await deps.sha256Hex(deps.canonicalJson({ requestReactId, previewId, proof }));
}

/** This projection excludes narration and later outcome fields. */
export function preparedUndoAction(value: unknown): PreparedUndoAction {
  const status = prop(value, "status");
  if (prop(value, "action") !== "undo_component" || status !== "pending" && status !== "completed"
    || prop(prop(prop(value, "undoTarget"), "undo"), "state") !== "ready") throw new Error("Invalid saved undo preparation");
  const target = prop(value, "undoTarget"), captured = prop(target, "undo"), source = prop(captured, "source"), request = prop(value, "undoRequest");
  if (!source) throw new Error("Missing undo source");
  const workflowResultId = prop(source, "workflowResultId");
  return { action: "undo_component", status: "pending", componentId: prop(value, "componentId"),
    undoRequest: { reactId: prop(request, "reactId"), previewId: prop(request, "previewId") },
    undoTarget: { ownerId: prop(target, "ownerId"), companyId: prop(target, "companyId"), componentId: prop(target, "componentId"),
      undo: { source: { jobId: prop(source, "jobId"), ...(workflowResultId !== undefined ? { workflowResultId } : {}),
        sourceFingerprint: prop(source, "sourceFingerprint"), effectId: prop(source, "effectId") } as UndoSource, workspaceSceneId: prop(captured, "workspaceSceneId"),
        fingerprint: prop(captured, "fingerprint"), state: "ready" } } };
}

/**
 * A later request's turn row is usable for undo: owned, live, not a provider
 * message and not cancelled. The checks read row metadata only.
 */
export function undoRequestRowUsable(doc: unknown, userId: string, companyId: string): boolean {
  const requestState = prop(doc, "requestState") ?? "";
  return !!doc && prop(doc, "userId") === userId && prop(doc, "companyId") === companyId && !prop(doc, "isDeleted")
    && !prop(doc, "providerAccountKey") && !prop(doc, "requestInterruptedAt") && requestState !== "cancelled" && requestState !== "interrupted";
}

/** The content a finalization reads from the later request's turn. */
export type FinalizeUndoContent = {
  fetchedData: unknown; extraFields: unknown; completionUndoVerification?: UndoVerificationCheckpoint | null;
  textAnswer?: unknown; audioAnswer?: unknown;
};
export type FinalizeUndoRequest = UndoSource & {
  userId: string; companyId: string; requestReactId: string; previewId: string; componentId: string; completionText: string;
};

/**
 * Finalization, first half: the request's one matching action and receipt,
 * the proof, the completed action and receipt, and which sibling actions
 * this batch must prove (positions `[start, end)` other than the action's
 * own). `undo` is the effect's stored undo entry, already checked complete.
 */
export async function planFinalizeUndo(request: FinalizeUndoRequest, workspaceSceneId: unknown,
  undo: { appliedFingerprint?: string; undoneFingerprint: string; undoneAt: number }, content: FinalizeUndoContent, deps: CompletionUndoDeps) {
  const data: unknown[] = Array.isArray(content.fetchedData) ? content.fetchedData : [];
  if (!data.length || data.length > 1000) throw new Error("Undo request actions unavailable");
  if (data.filter((action) => prop(prop(action, "undoRequest"), "previewId") === request.previewId).length !== 1) throw new Error("Undo request preview is ambiguous");
  const matches = data.map((action, index) => ({ action, index })).filter(({ action }) => {
    const target = prop(action, "undoTarget"), captured = prop(target, "undo"), source = prop(captured, "source"), undoRequest = prop(action, "undoRequest");
    return prop(action, "action") === "undo_component" && prop(action, "componentId") === request.componentId
      && prop(target, "ownerId") === request.userId && prop(target, "companyId") === request.companyId && prop(target, "componentId") === request.componentId
      && prop(captured, "workspaceSceneId") === workspaceSceneId && prop(captured, "fingerprint") === undo.appliedFingerprint && prop(captured, "state") === "ready"
      && prop(source, "jobId") === request.jobId && prop(source, "workflowResultId") === request.workflowResultId
      && prop(source, "sourceFingerprint") === request.sourceFingerprint && prop(source, "effectId") === request.effectId
      && prop(undoRequest, "reactId") === request.requestReactId && prop(undoRequest, "previewId") === request.previewId;
  });
  if (matches.length !== 1) throw new Error("Undo request source changed");
  const { action, index } = matches[0];
  const proof: UndoProof = { jobId: request.jobId, sourceFingerprint: request.sourceFingerprint, effectId: request.effectId,
    componentId: request.componentId, fingerprint: undo.undoneFingerprint, completedAt: undo.undoneAt };
  const digest = await undoProofDigest(request.requestReactId, request.previewId, proof, deps);
  const summary = prop(content.extraFields, "actionExecutionSummary");
  const receipts = prop(prop(summary, "trust"), "receipts");
  if (!Array.isArray(receipts) || receipts.length > 1000) throw new Error("Undo request receipt unavailable");
  const matchingReceipts = receipts.filter((value: unknown) => prop(value, "previewId") === request.previewId);
  if (matchingReceipts.length !== 1) throw new Error("Undo request receipt changed");
  const receipt: unknown = matchingReceipts[0];
  const replayed = prop(action, "status") === "completed" && prop(prop(action, "undoOutcome"), "digest") === digest
    && prop(receipt, "status") === "succeeded" && prop(prop(receipt, "evidence"), "resultDigest") === `sha256:${digest}`;
  if (!replayed && (prop(action, "status") !== "pending" || prop(receipt, "status") !== "accepted")) throw new Error("Undo request outcome changed");
  const completedAt = new Date(undo.undoneAt).toISOString();
  const evidence = prop(receipt, "evidence"), references = prop(evidence, "references");
  const completedReceipt = { ...loose(receipt), receiptId: `receipt_${digest.slice(0, 20)}`, status: "succeeded", message: request.completionText,
    idempotency: { ...loose(prop(receipt, "idempotency")), state: "succeeded" }, timing: { ...loose(prop(receipt, "timing")), completedAt },
    evidence: { ...loose(evidence), resultDigest: `sha256:${digest}`, references: Array.from(new Set([
      ...(Array.isArray(references) ? references : []), `undo:${digest}`])) } };
  const fetchedData: unknown[] = replayed ? data : data.map((value, at) => at === index
    ? { ...loose(value), status: "completed", textAnswer: request.completionText, audioAnswer: "", undoOutcome: { ...proof, digest } } : value);
  const finalReceipts: unknown[] = replayed ? receipts : receipts.map((value: unknown) => value === receipt ? completedReceipt : value);
  const extraFields: Loose = { ...loose(content.extraFields), actionExecutionSummary: { ...loose(summary),
    trust: { ...loose(prop(summary, "trust")), receipts: finalReceipts } } };
  const allCompleted = fetchedData.every((value) => prop(value, "action") === "undo_component" && prop(value, "status") === "completed" && !!prop(prop(value, "undoOutcome"), "digest"))
    && new Set(fetchedData.map((value) => prop(prop(value, "undoRequest"), "previewId"))).size === fetchedData.length;
  let generation: string | null = null, start = 0, end = 0;
  if (allCompleted) {
    // The checkpoint records verified receipts, not browser completion labels.
    // Changing any action or receipt invalidates all previous batch progress.
    generation = await deps.undoGeneration({ ownerId: request.userId, companyId: request.companyId,
      requestReactId: request.requestReactId, fetchedData, receipts: finalReceipts });
    const checkpoint = content.completionUndoVerification;
    start = checkpoint?.version === 1 && checkpoint.generation === generation
      && Number.isSafeInteger(checkpoint.nextIndex) && checkpoint.nextIndex >= 0 && checkpoint.nextIndex <= fetchedData.length
      ? checkpoint.nextIndex : 0;
    end = Math.min(start + 20, fetchedData.length);
  }
  const siblings: unknown[] = [];
  for (let at = start; at < end; at++) if (at !== index) siblings.push(fetchedData[at]);
  return { proof, digest, replayed, fetchedData, finalReceipts, extraFields, allCompleted, generation, end, siblings };
}
export type FinalizeUndoPlan = Awaited<ReturnType<typeof planFinalizeUndo>>;

/**
 * A sibling action's captured source, or null when its identity does not
 * hold. A source that is not text cannot name a job, so it is null too.
 */
export function siblingUndoSource(value: unknown, request: { userId: string; companyId: string; requestReactId: string }): UndoSource | null {
  const target = prop(value, "undoTarget"), other = prop(prop(target, "undo"), "source");
  if (prop(target, "ownerId") !== request.userId || prop(target, "companyId") !== request.companyId || prop(target, "componentId") !== prop(value, "componentId")
    || prop(prop(value, "undoRequest"), "reactId") !== request.requestReactId || !other) return null;
  const jobId = prop(other, "jobId"), workflowResultId = prop(other, "workflowResultId"), sourceFingerprint = prop(other, "sourceFingerprint"), effectId = prop(other, "effectId");
  if (typeof jobId !== "string" || typeof sourceFingerprint !== "string" || typeof effectId !== "string"
    || workflowResultId !== undefined && typeof workflowResultId !== "string") return null;
  return { jobId, ...(workflowResultId !== undefined ? { workflowResultId } : {}), sourceFingerprint, effectId };
}

/**
 * Never trust a sibling's stored completion label as evidence of its write:
 * its effect's stored undo, read fresh through `current()`, must prove the
 * digest the action and its receipt carry.
 */
export async function siblingUndoProven(value: unknown, other: UndoSource, verified: UndoJobView,
  request: { companyId: string; requestReactId: string }, finalReceipts: unknown[], deps: CompletionComputeDeps) {
  const captured = prop(prop(value, "undoTarget"), "undo"), componentId = prop(value, "componentId");
  const stored = verified.undo, delivered = verified.delivery;
  if (verified.companyId !== request.companyId || verified.workspaceSceneId !== prop(captured, "workspaceSceneId")
    || !stored?.undoneFingerprint || !Number.isFinite(stored.undoneAt) || stored.componentId !== componentId
    || prop(captured, "fingerprint") !== stored.appliedFingerprint || prop(captured, "state") !== "ready"
    || !delivered || delivered.componentIds.length !== 1 || delivered.componentIds[0] !== componentId) return false;
  const previewId = prop(prop(value, "undoRequest"), "previewId") as string;
  const otherDigest = await undoProofDigest(request.requestReactId, previewId, { jobId: other.jobId, sourceFingerprint: other.sourceFingerprint,
    effectId: other.effectId, componentId: componentId as string, fingerprint: stored.undoneFingerprint, completedAt: stored.undoneAt as number }, deps);
  // A missing receipt entry throws, as it always has: the batch is not proven.
  const evidence = finalReceipts.filter((entry) => (entry as Loose).previewId === previewId);
  return prop(prop(value, "undoOutcome"), "digest") === otherDigest && evidence.length === 1 && prop(evidence[0], "status") === "succeeded"
    && prop(prop(evidence[0], "evidence"), "resultDigest") === `sha256:${otherDigest}`;
}

/**
 * Whether every sibling of this batch is proven, in order, stopping at the
 * first that is not. `verify(at, source)` reads sibling `at`'s job-side
 * state through `current()`; null means that read failed. Only `verify`
 * may throw out of here, so a caller can abort on a stale snapshot.
 */
export async function proveUndoSiblings(plan: FinalizeUndoPlan, request: { userId: string; companyId: string; requestReactId: string },
  verify: (at: number, source: UndoSource) => Promise<UndoJobView | null>, deps: CompletionComputeDeps): Promise<boolean> {
  for (let at = 0; at < plan.siblings.length; at++) {
    const value = plan.siblings[at], source = siblingUndoSource(value, request);
    if (!source) return false;
    const verified = await verify(at, source);
    if (!verified) return false;
    try {
      if (!await siblingUndoProven(value, source, verified, request, plan.finalReceipts, deps)) return false;
    } catch { return false; }
  }
  return true;
}

/**
 * Finalization, second half: the checkpoint, the continuation, the turn's
 * answer once every action is proven, and whether anything changed.
 * `siblingsProven` is whether each of `plan.siblings` was proven.
 */
export async function finishFinalizeUndo(plan: FinalizeUndoPlan, siblingsProven: boolean, content: FinalizeUndoContent,
  request: { requestReactId: string; completionText: string }, deps: CompletionComputeDeps) {
  const allUndone = plan.allCompleted && siblingsProven;
  let continuation: { generation: string; verified: number; total: number } | undefined;
  let completionUndoVerification: UndoVerificationCheckpoint | null = null;
  if (allUndone) {
    completionUndoVerification = { version: 1, generation: plan.generation as string, nextIndex: plan.end };
    if (plan.end < plan.fetchedData.length) continuation = { generation: plan.generation as string, verified: plan.end, total: plan.fetchedData.length };
  }
  const text = allUndone && !continuation ? request.completionText : undefined;
  const extraFields: Loose = text ? { ...plan.extraFields, audioCompletionKey: `undo:${request.requestReactId}:${await deps.sha256Hex(deps.canonicalJson(
    plan.fetchedData.map((value) => prop(prop(value, "undoOutcome"), "digest")), COMPLETION_UNDO_AGGREGATE_BYTES))}` } : plan.extraFields;
  const unchanged = plan.replayed && deps.canonicalJson(completionUndoVerification) === deps.canonicalJson(content.completionUndoVerification ?? null)
    && deps.canonicalJson(extraFields, COMPLETION_UNDO_AGGREGATE_BYTES) === deps.canonicalJson(content.extraFields, COMPLETION_UNDO_AGGREGATE_BYTES)
    && (!text || content.textAnswer === text && content.audioAnswer === text);
  return { unchanged, continuation, completionUndoVerification, text, fetchedData: plan.fetchedData, extraFields };
}
export type FinalizeUndoFinish = Awaited<ReturnType<typeof finishFinalizeUndo>>;

/**
 * The finalization result as Node hands it to the silo and the silo
 * recomputes it on a plaintext turn: canonical JSON, compared byte for byte.
 */
export function finalizeUndoResultJson(finish: FinalizeUndoFinish, deps: CompletionComputeDeps): string {
  return deps.canonicalJson({ outcome: finish.unchanged ? "replayed" : "applied", fetchedData: finish.fetchedData, extraFields: finish.extraFields,
    completionUndoVerification: finish.completionUndoVerification, text: finish.text ?? null, continuation: finish.continuation ?? null }, COMPLETION_UNDO_RESULT_BYTES);
}

/** A registration binding: the request turn, its preview, and the scene it was prepared in. */
export type UndoRegistrationBinding = { userId: string; companyId: string; requestReactId: string; previewId: string; workspaceSceneId: unknown; digest: string };

/** The saved action still is the one the registration prepared. */
export function checkUndoRegistrationAction(binding: UndoRegistrationBinding, prepared: unknown, action: unknown, deps: CompletionComputeDeps) {
  const capturedAction = preparedUndoAction(action), target = capturedAction.undoTarget, captured = target.undo;
  if (deps.canonicalJson(capturedAction) !== deps.canonicalJson(prop(prepared, "action")) || target.ownerId !== binding.userId || target.companyId !== binding.companyId
    || target.componentId !== capturedAction.componentId || capturedAction.undoRequest.reactId !== binding.requestReactId
    || capturedAction.undoRequest.previewId !== binding.previewId || captured.workspaceSceneId !== binding.workspaceSceneId) throw new Error("Undo registration identity changed");
  return capturedAction;
}

/**
 * Registration, content first half: the request turn's one action and one
 * receipt for the preview, and the action still as prepared.
 */
export function undoRegistrationEvidence(binding: UndoRegistrationBinding, prepared: unknown, doc: unknown, deps: CompletionComputeDeps) {
  if (!undoRequestRowUsable(doc, binding.userId, binding.companyId)) throw new Error("Undo request unavailable");
  const actions: unknown[] = Array.isArray(prop(doc, "fetchedData")) ? prop(doc, "fetchedData") as unknown[] : [];
  const receipts = prop(prop(prop(prop(doc, "extraFields"), "actionExecutionSummary"), "trust"), "receipts");
  if (!actions.length || actions.length > 1000 || !Array.isArray(receipts) || receipts.length > 1000) throw new Error("Undo evidence unavailable");
  const selected = actions.filter((value) => prop(prop(value, "undoRequest"), "previewId") === binding.previewId);
  const matching = receipts.filter((value: unknown) => prop(value, "previewId") === binding.previewId);
  if (selected.length !== 1 || matching.length !== 1) throw new Error("Ambiguous undo evidence");
  const action: unknown = selected[0], receipt: unknown = matching[0];
  return { action, receipt, capturedAction: checkUndoRegistrationAction(binding, prepared, action, deps) };
}
export type UndoRegistrationEvidence = ReturnType<typeof undoRegistrationEvidence>;

/**
 * The job-side half of a registration, read by the silo's `current()` for
 * the prepared source: the effect's stored undo, the original turn's row id
 * (never the request's own) and the component's state.
 */
export type UndoRegistrationJob = { conversationId: string; undo: UndoEntry; state: "ready" | "undone" | "changed" };

/**
 * Registration, content second half: a pending action still carries its
 * prepared accepted receipt, a completed one the proof of the effect's
 * stored undo; then the request revision and fingerprint recovery replays
 * against.
 */
export async function undoRegistrationResult(binding: UndoRegistrationBinding, prepared: unknown, doc: unknown,
  evidence: UndoRegistrationEvidence, job: UndoRegistrationJob, deps: CompletionComputeDeps) {
  const { action, receipt, capturedAction } = evidence, undo = job.undo;
  if (job.conversationId === prop(doc, "_id")) throw new Error("Undo source changed");
  let outcome: Loose | undefined;
  if (prop(action, "status") === "pending") {
    if (prop(receipt, "status") !== "accepted" || deps.canonicalJson(receipt) !== deps.canonicalJson(prop(prepared, "receipt"))
      || prop(action, "undoOutcome") !== undefined) throw new Error("Undo accepted receipt changed");
  } else {
    if (!undo.undoneFingerprint || !Number.isFinite(undo.undoneAt)) throw new Error("Undo has not completed");
    const source = capturedAction.undoTarget.undo.source;
    const proof: UndoProof = { jobId: source.jobId, sourceFingerprint: source.sourceFingerprint, effectId: source.effectId,
      componentId: capturedAction.componentId as string, fingerprint: undo.undoneFingerprint, completedAt: undo.undoneAt as number };
    const digest = await undoProofDigest(binding.requestReactId, binding.previewId, proof, deps);
    outcome = { ...proof, digest };
    if (deps.canonicalJson(prop(action, "undoOutcome")) !== deps.canonicalJson(outcome) || prop(receipt, "status") !== "succeeded"
      || prop(receipt, "receiptId") !== `receipt_${digest.slice(0, 20)}` || prop(prop(receipt, "evidence"), "resultDigest") !== `sha256:${digest}`) throw new Error("Undo completion proof changed");
  }
  const requestRevision = prop(doc, "completionRevision") ?? 0;
  if (typeof requestRevision !== "number" || !Number.isSafeInteger(requestRevision) || requestRevision < 0) throw new Error("Undo request revision changed");
  return { requestReactId: binding.requestReactId, previewId: binding.previewId, registrationDigest: binding.digest,
    requestRevision, requestFingerprint: await deps.sha256Hex(deps.canonicalJson({ action, receipt, revision: requestRevision }, COMPLETION_UNDO_AGGREGATE_BYTES)),
    updatedDate: prop(doc, "updatedDate") ?? prop(doc, "createdDate") ?? 0, state: job.state,
    action: { ...capturedAction, status: prop(action, "status"), ...(outcome ? { undoOutcome: outcome } : {}) }, receipt };
}
export type UndoRegistrationResult = Awaited<ReturnType<typeof undoRegistrationResult>>;
/**
 * A job's action acceptance merged into its request turn's saved summary
 * (`extraFields.actionExecutionSummary`, jobActionAcceptance.ts.template).
 * The silo runs it on a plaintext turn and Node on an opened sealed one.
 * `summary` is the acceptance the silo checks against the job and on its
 * own; its previews and receipts are merged by preview id, a receipt's job
 * references unioned, and a finalized receipt is never downgraded. A replay
 * proves the saved summary already holds this job's acceptance and writes
 * nothing (null). Throws as the silo refuses.
 */
export function mergedJobActionAcceptance(existing: unknown, summary: unknown, jobId: string, replayed: boolean,
  canonicalJson: (value: unknown) => string): Loose | null {
  const trust = prop(summary, "trust"), saved = prop(existing, "trust");
  const previews = prop(trust, "previews"), receipts = prop(trust, "receipts");
  const previousPreviews = prop(saved, "previews") ?? [], previousReceipts = prop(saved, "receipts") ?? [];
  if (!Array.isArray(previews) || !Array.isArray(receipts)) throw new Error("Invalid job acceptance");
  if (!Array.isArray(previousPreviews) || !Array.isArray(previousReceipts)) throw new Error("Invalid saved job acceptance");
  const nextPreviews: unknown[] = [...previousPreviews], nextReceipts: unknown[] = [...previousReceipts];
  for (const preview of previews) {
    const previewId = prop(preview, "previewId");
    const receipt = receipts.find((value: unknown) => prop(value, "previewId") === previewId);
    const knownPreviews = nextPreviews.filter((value) => prop(value, "previewId") === previewId);
    const knownReceipts = nextReceipts.filter((value) => prop(value, "previewId") === previewId);
    if (knownPreviews.length > 1 || knownReceipts.length > 1
      || knownPreviews.some((value) => canonicalJson(value) !== canonicalJson(preview))
      || knownReceipts.some((value) => prop(value, "actionId") !== prop(receipt, "actionId") || prop(value, "functionName") !== prop(receipt, "functionName")
        || canonicalJson({ ...loose(prop(value, "idempotency")), state: "accepted" }) !== canonicalJson(prop(receipt, "idempotency")))) throw new Error("Saved job acceptance identity changed");
    const known = knownReceipts[0];
    if (replayed) {
      const references = prop(prop(known, "evidence"), "references");
      if (knownPreviews.length !== 1 || knownReceipts.length !== 1
        || !Array.isArray(references) || !references.includes(`jobId:${jobId}`)) throw new Error("Saved job acceptance is unavailable");
      continue;
    }
    if (knownReceipts.length && prop(known, "status") !== "accepted") throw new Error("Action receipt is already finalized");
    if (!knownPreviews.length) nextPreviews.push(preview);
    if (!knownReceipts.length) nextReceipts.push(receipt);
    else {
      const evidence = prop(known, "evidence"), previous = prop(evidence, "references"), added = prop(prop(receipt, "evidence"), "references");
      const references = Array.from(new Set([...(Array.isArray(previous) ? previous : []), ...(Array.isArray(added) ? added : [])]));
      if (references.length > 1000) throw new Error("Job acceptance reference limit reached");
      nextReceipts[nextReceipts.indexOf(known)] = { ...loose(known), evidence: { ...loose(evidence), references } };
    }
    // A later or replayed job must never downgrade a finalized action receipt.
  }
  if (nextPreviews.length > 1000 || nextReceipts.length > 1000) throw new Error("Job acceptance limit reached");
  if (replayed) return null;
  return { ...loose(summary), ...loose(existing), trust: { ...loose(trust), ...loose(saved), previews: nextPreviews, receipts: nextReceipts } };
}

/** Whether a turn write advances its completion revision: the turn has one, or holds workflow completions. */
export function advancesCompletionRevision(turn: unknown): boolean {
  const completions = prop(prop(turn, "extraFields"), "workflowCompletions");
  return prop(turn, "completionRevision") !== undefined || (Array.isArray(completions) && completions.length > 0);
}
// COMPLETION_COMPUTE_END
